/**
 * components/AcademicPerformance.tsx
 * ------------------------------------------------------------------
 * Round 12 — shared performance view.
 *
 * Renders the same shape of academic dashboard the staff Student
 * Profile shows on its first tab — but driven by props so the parent
 * portal can pass a filtered view that:
 *   - hides the position/rank stat
 *   - shows the teacher next to each subject
 *   - filters out unpublished CAs / unpublished term-finals
 *
 * The component itself doesn't query Firestore — callers pass in
 * already-loaded marks docs + publishing state. Keeps it pure and
 * testable.
 *
 * Round 12 deliberately does NOT migrate the staff Student Profile
 * to call this component yet (that's a separate, reversible refactor).
 * Parent Portal is the only caller for now.
 * ------------------------------------------------------------------
 */
import { useMemo } from 'react';
import {
  TrendingUp,
  TrendingDown,
  Minus,
  AlertTriangle,
  CheckCircle2,
  type LucideIcon,
} from 'lucide-react';
import { Card } from '@/components/ui/primitives';
import { buildTranscript, type TranscriptGrid } from '@/lib/transcript';
import { deriveMark } from '@/lib/grading';
import type {
  Student,
  Subject,
  Teacher,
  MarksDocument,
  TermStatusDocument,
  MaybeMark,
} from '@/types';

export interface AcademicPerformanceProps {
  student: Student;
  subjects: Subject[];
  teachers: Teacher[];
  marksDocs: MarksDocument[];
  /**
   * Term status docs across all the (year, term) combos the student
   * has marks for. Used for the publish filtering. Parent mode
   * requires this; staff mode (when added later) can pass empty.
   */
  termStatuses: TermStatusDocument[];
  /**
   * Whether class size + class average should be computed.
   * Caller provides the full class roster for this calculation.
   */
  classmates?: Student[];
  /**
   * Pre-computed per-subject class average (anonymous aggregate).
   * When supplied, it is used directly and the "Class avg" column is
   * shown without needing the full classmate roster — this is how the
   * parent portal renders averages while only receiving the child's
   * own marks (see getChildAcademics).
   */
  classAvgBySubject?: Record<string, number>;
  /**
   * 'parent' mode: filter unpublished CAs and term-finals; hide rank;
   * show teacher per subject; show class average column per term.
   * 'staff' mode (not used yet): show everything.
   */
  mode: 'parent' | 'staff';
}

interface QuickStats {
  currentAvg: number | null;
  trend: number | null;
  passed: number;
  failed: number;
  isAtRisk: boolean;
  /** Only populated in staff mode. */
  rank?: number | null;
  classSize?: number;
  /** Per-subject classmates' average — populated when classmates supplied. */
  classAvgBySubject?: Record<string, number>;
}

/**
 * Apply Round 12 publish filtering to a marks doc, producing a
 * shallow-cloned doc where only published CAs (and optionally the
 * exam/term-final) remain in each entry's `ca` map.
 *
 * Returns null when the term-status doc says nothing is published —
 * the doc gets skipped entirely. This keeps unpublished terms out
 * of the transcript grid (no empty columns).
 */
function filterMarksDocForParent(
  md: MarksDocument,
  status: TermStatusDocument | undefined,
): MarksDocument | null {
  if (!status) return null;
  const published = status.publishedCas ?? {};
  const termFinalPublished = !!status.publishedTermFinal;
  const publishedCaLabels = new Set(Object.keys(published));
  // If neither CAs nor term-final are published, hide the whole doc.
  if (publishedCaLabels.size === 0 && !termFinalPublished) return null;

  const newEntries: MarksDocument['entries'] = {};
  for (const [studentId, entry] of Object.entries(md.entries ?? {})) {
    const ca: typeof entry.ca = {};
    for (const [label, mark] of Object.entries(entry.ca ?? {})) {
      if (publishedCaLabels.has(label)) ca[label] = mark;
    }
    const exam = termFinalPublished ? entry.exam : undefined;
    if (Object.keys(ca).length === 0 && exam === undefined) continue;
    newEntries[studentId] = { ...entry, ca, exam };
  }
  if (Object.keys(newEntries).length === 0) return null;

  return { ...md, entries: newEntries };
}

export default function AcademicPerformance({
  student,
  subjects,
  teachers,
  marksDocs,
  termStatuses,
  classmates,
  classAvgBySubject: classAvgProp,
  mode,
}: AcademicPerformanceProps) {
  // Index status docs by ${year}_${term}
  const statusByTermKey = useMemo(() => {
    const map = new Map<string, TermStatusDocument>();
    for (const s of termStatuses) {
      map.set(`${s.year}_${s.term}`, s);
    }
    return map;
  }, [termStatuses]);

  // Apply parent-mode publish filtering, then build the transcript.
  const filteredMarksDocs: MarksDocument[] = useMemo(() => {
    if (mode === 'staff') return marksDocs;
    const out: MarksDocument[] = [];
    for (const md of marksDocs) {
      const status = statusByTermKey.get(`${md.year}_${md.term}`);
      const filtered = filterMarksDocForParent(md, status);
      if (filtered) out.push(filtered);
    }
    return out;
  }, [mode, marksDocs, statusByTermKey]);

  const transcript: TranscriptGrid = useMemo(
    () =>
      buildTranscript({
        studentId: student.id,
        marksDocs: filteredMarksDocs,
        subjects,
      }),
    [student.id, filteredMarksDocs, subjects],
  );

  // Compute class average per subject for the latest term (parent
  // mode shows this; gives anonymous calibration without revealing
  // individual ranks).
  const computedClassAvg = useMemo(() => {
    const out: Record<string, number> = {};
    if (!classmates || classmates.length === 0) return out;
    const latestTerm = transcript.terms[transcript.terms.length - 1];
    if (!latestTerm) return out;
    // Find the matching marks doc for this term
    const latestMd = filteredMarksDocs.find(
      (md) => md.year === latestTerm.year && md.term === latestTerm.term,
    );
    if (!latestMd) return out;
    // For each subject, average across all classmates in the doc
    for (const subj of transcript.subjects) {
      // Only marks docs for this subject matter
      if (latestMd.subjectId !== subj.id) continue;
      let total = 0;
      let count = 0;
      for (const cm of classmates) {
        const entry = latestMd.entries?.[cm.id];
        if (!entry) continue;
        // Numeric-only: uses d.termMark (the CA/term average), which is
        // scheme-independent — no gradeScheme needed here.
        const d = deriveMark(
          latestTerm.term,
          entry.ca,
          entry.exam as MaybeMark | undefined,
        );
        if (typeof d.termMark === 'number') {
          total += d.termMark;
          count += 1;
        }
      }
      if (count > 0) {
        out[subj.id] = Math.round(total / count);
      }
    }
    return out;
  }, [classmates, filteredMarksDocs, transcript]);

  // Prefer a server-supplied anonymous average (parent portal); else
  // use the locally-computed one (when full classmate roster present).
  const classAvgBySubject = classAvgProp ?? computedClassAvg;
  const showClassAvg = !!classAvgProp || !!(classmates && classmates.length > 0);

  // Quick stats — minimal version (current avg, trend, pass/fail).
  // Position/rank intentionally omitted in parent mode.
  const quickStats: QuickStats | null = useMemo(() => {
    if (transcript.terms.length === 0) return null;
    const latestTerm = transcript.terms[transcript.terms.length - 1];
    const prevTerm =
      transcript.terms.length >= 2
        ? transcript.terms[transcript.terms.length - 2]
        : null;
    let passed = 0;
    let failed = 0;
    let isAtRisk = false;
    for (const subj of transcript.subjects) {
      const cell = latestTerm.cells[subj.id];
      if (!cell || cell.mark === null) continue;
      if (cell.mark >= 50) passed += 1;
      else {
        failed += 1;
        if (cell.mark < 40) isAtRisk = true;
      }
    }
    const trend =
      prevTerm?.termAverage != null && latestTerm.termAverage != null
        ? latestTerm.termAverage - prevTerm.termAverage
        : null;
    return {
      currentAvg: latestTerm.termAverage,
      trend,
      passed,
      failed,
      isAtRisk,
      classSize: classmates?.length,
      classAvgBySubject,
    };
  }, [transcript, classmates, classAvgBySubject]);

  // Title + surname only (e.g. "Mr Chambwera") — full names overlap the marks.
  const teacherName = (id?: string) => {
    const t = teachers.find((x) => x.id === id);
    return t ? `${t.title ?? ''} ${t.lastName ?? ''}`.trim() || '—' : '—';
  };

  // Latest term used for the "per-subject grid" — when no terms are
  // published yet, this view shows the empty state.
  const latestTerm = transcript.terms[transcript.terms.length - 1];

  if (transcript.terms.length === 0) {
    return (
      <Card className="p-6 text-center">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          No marks have been published yet.
          {mode === 'parent' && (
            <>
              {' '}
              Once your child&apos;s teachers complete and publish each
              continuous assessment, the marks will appear here.
            </>
          )}
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-5">
      {/* Quick stats */}
      {quickStats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat
            label="Current Average"
            value={
              quickStats.currentAvg !== null
                ? `${quickStats.currentAvg}%`
                : '—'
            }
            tone={
              quickStats.currentAvg !== null && quickStats.currentAvg >= 50
                ? 'emerald'
                : 'red'
            }
          />
          <Stat
            label="Trend"
            value={
              quickStats.trend !== null
                ? `${quickStats.trend >= 0 ? '+' : ''}${quickStats.trend}%`
                : '—'
            }
            tone={
              quickStats.trend !== null
                ? quickStats.trend >= 0
                  ? 'emerald'
                  : 'red'
                : 'gray'
            }
            icon={
              quickStats.trend !== null
                ? quickStats.trend > 0
                  ? TrendingUp
                  : quickStats.trend < 0
                    ? TrendingDown
                    : Minus
                : undefined
            }
          />
          <Stat
            label="Subjects Passed"
            value={`${quickStats.passed}/${quickStats.passed + quickStats.failed}`}
            tone={quickStats.failed === 0 ? 'emerald' : 'gold'}
          />
          <Stat
            label="Status"
            value={quickStats.isAtRisk ? 'Needs Support' : 'On Track'}
            tone={quickStats.isAtRisk ? 'red' : 'emerald'}
            icon={quickStats.isAtRisk ? AlertTriangle : CheckCircle2}
          />
        </div>
      )}

      {/* Per-subject grid for the latest published term */}
      {latestTerm && (
        <Card className="overflow-hidden">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="font-bold text-gray-800 dark:text-white">
              Subjects · most recent results
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              The latest mark attained in each subject
              {mode === 'parent' ? ", and how it compares to the class average" : ''}.
              Anything not from {latestTerm.label} is labelled with the term it comes from.
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800/50 text-left text-gray-500">
                <tr>
                  <th className="px-3 py-2.5 font-medium">Subject</th>
                  <th className="px-3 py-2.5 font-medium">Teacher</th>
                  <th className="px-3 py-2.5 font-medium text-center">
                    Mark
                  </th>
                  <th className="px-3 py-2.5 font-medium text-center">
                    Grade
                  </th>
                  {showClassAvg && (
                    <th className="px-3 py-2.5 font-medium text-center">
                      Class avg
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {transcript.subjects.map((subj) => {
                  /* THE MOST RECENT RESULT ATTAINED, not "the mark in the
                     newest column". A subject dropped after Term 1, an exam
                     missed, or a class whose marking is not finished used to
                     render a dash here — as though the pupil had never sat
                     it. The mark was on file one column further left. */
                  const cell = transcript.latestBySubject[subj.id];
                  const stale = !!cell && !cell.isCurrentTerm;
                  const classAvg = classAvgBySubject[subj.id];
                  return (
                    <tr key={subj.id}>
                      <td className="px-3 py-2 font-medium">
                        {subj.name}
                        {stale && (
                          /* Never show the figure without its term — a Term 1
                             mark presented bare reads as this term's. */
                          <span className="ml-2 text-[10px] font-normal px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 whitespace-nowrap">
                            {cell.label}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400">
                        {teacherName(student.subjectTeachers?.[subj.id])}
                      </td>
                      <td className="px-3 py-2 text-center">
                        {cell?.mark !== undefined && cell?.mark !== null
                          ? `${cell.mark}%`
                          : '–'}
                      </td>
                      <td className="px-3 py-2 text-center">
                        {cell && cell.grade !== '-' ? (
                          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-gray-200 dark:bg-gray-700">
                            {cell.grade}
                          </span>
                        ) : (
                          <span className="text-gray-300">–</span>
                        )}
                      </td>
                      {showClassAvg && (
                        <td className="px-3 py-2 text-center text-xs text-gray-600 dark:text-gray-400">
                          {classAvg !== undefined ? `${classAvg}%` : '–'}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Cross-term transcript grid — only useful with 2+ terms */}
      {transcript.terms.length >= 2 && (
        <Card className="overflow-hidden">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="font-bold text-gray-800 dark:text-white">
              Across terms
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              One row per subject, one column per published term.
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800/50 text-left text-gray-500">
                <tr>
                  <th className="px-3 py-2.5 font-medium">Subject</th>
                  {transcript.terms.map((t) => (
                    <th
                      key={`${t.year}-${t.term}`}
                      className="px-3 py-2.5 font-medium text-center"
                    >
                      {t.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {transcript.subjects.map((subj) => (
                  <tr key={subj.id}>
                    <td className="px-3 py-2 font-medium">{subj.name}</td>
                    {transcript.terms.map((t) => {
                      const cell = t.cells[subj.id];
                      return (
                        <td
                          key={`${t.year}-${t.term}`}
                          className="px-3 py-2 text-center"
                        >
                          {cell ? (
                            <span className="inline-flex items-center gap-1.5">
                              <span className="text-sm">
                                {cell.mark === null ? '–' : `${cell.mark}%`}
                              </span>
                              {cell.grade !== '-' && (
                                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-gray-200 dark:bg-gray-700">
                                  {cell.grade}
                                </span>
                              )}
                            </span>
                          ) : (
                            <span className="text-gray-300">–</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
                <tr className="bg-primary/5 font-semibold">
                  <td className="px-3 py-2">Term average</td>
                  {transcript.terms.map((t) => (
                    <td
                      key={`${t.year}-${t.term}-avg`}
                      className="px-3 py-2 text-center"
                    >
                      {t.termAverage === null ? '–' : `${t.termAverage}%`}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
            {transcript.overallAverage !== null && (
              <div className="px-4 py-3 bg-primary/10 text-primary font-semibold text-sm">
                Overall average across {transcript.terms.length} term(s):{' '}
                {transcript.overallAverage}%
              </div>
            )}
          </div>
        </Card>
      )}
    </div>
  );
}

/* ==================================================================
   Stat — small card used for the quick-stats row
   ================================================================== */

type StatTone = 'emerald' | 'red' | 'gold' | 'gray' | 'blue';
const TONE_CLASSES: Record<StatTone, string> = {
  emerald: 'text-emerald-700 dark:text-emerald-300',
  red: 'text-red-700 dark:text-red-300',
  gold: 'text-amber-700 dark:text-amber-300',
  gray: 'text-gray-600 dark:text-gray-300',
  blue: 'text-blue-700 dark:text-blue-300',
};

function Stat({
  label,
  value,
  tone,
  icon: Icon,
}: {
  label: string;
  value: string;
  tone: StatTone;
  icon?: LucideIcon;
}) {
  return (
    <Card className="p-3">
      <div className="flex items-center gap-2 mb-1">
        {Icon && <Icon size={14} className={TONE_CLASSES[tone]} />}
        <p className="text-[10px] uppercase tracking-wide font-semibold text-gray-500">
          {label}
        </p>
      </div>
      <p className={`text-xl font-bold ${TONE_CLASSES[tone]}`}>{value}</p>
    </Card>
  );
}
